/**
 * Pure helpers for the public Voucher Redemption Locator (/voucher-locations). No React, no network,
 * so they are unit-tested directly (voucherLocator.test.ts).
 *
 * Who is listed, which partners a voucher can be redeemed at, and which fields are published are all
 * decided server-side (voucher_locator_search). Nothing here filters partners.
 */

export const LOCATOR_RADII = [25, 50, 100, 200] as const;
export type LocatorRadius = (typeof LOCATOR_RADII)[number];

export interface LocatorLocation {
  n: number;
  name: string;
  type: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  website: string | null;
  lat: number;
  lng: number;
  approximate: boolean;
  miles: number;
}

export type LocatorStatus =
  | "ok"
  | "not_found"
  | "redeemed"
  | "expired"
  | "unavailable"
  | "zip_not_found"
  | "invalid_request"
  | "rate_limited"
  | "no_holder_zip"
  | "error";

export interface LocatorResponse {
  status: LocatorStatus;
  mode?: "general" | "voucher";
  center?: { lat: number; lng: number };
  radius_miles?: number;
  total?: number;
  shown?: number;
  locations?: LocatorLocation[];
  error?: string;
  /** Arrival (auto) search only: the voucher holder's ZIP the server centered on. */
  auto_zip?: string;
  /** Location search only: the ZIP nearest the member's coordinates (server-derived, never stored). */
  near_zip?: string;
}

/** Member-facing copy for every non-ok status. Never mentions internal reasons. */
export function statusMessage(status: LocatorStatus): string | null {
  switch (status) {
    case "ok":
      return null;
    case "not_found":
      return "We couldn't find that voucher. Please use the link in your voucher email.";
    case "redeemed":
      return "This voucher has already been redeemed.";
    case "expired":
      return "This voucher has expired.";
    case "unavailable":
      return "This voucher isn't available for redemption right now.";
    case "zip_not_found":
      return "We couldn't find that ZIP code. Please check it and try again.";
    case "rate_limited":
      return "Too many searches. Please wait a little while and try again.";
    case "no_holder_zip":
      return null; // arrival search had nothing to center on: the page just waits, as before
    case "invalid_request":
      return "Enter a 5-digit ZIP code or use your location.";
    default:
      return "Search is temporarily unavailable. Please try again shortly.";
  }
}

/** Only ever link to http(s). The server already drops anything else; this is defence in depth. */
export function safeWebsite(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:"
      ? u.toString()
      : null;
  } catch {
    return null;
  }
}

/** "1201 Yale St, Houston, TX 77008", or "Houston, TX 77008" when the street is withheld. */
export function addressLine(
  loc: Pick<LocatorLocation, "address" | "city" | "state" | "zip">,
): string {
  const cityState = [loc.city, loc.state].filter(Boolean).join(", ");
  const tail = [cityState, loc.zip].filter(Boolean).join(" ");
  return [loc.address, tail].filter((s) => s && s.trim()).join(", ");
}

/**
 * Partner coordinates are ZIP-centroid geocodes, so several partners in one ZIP share an exact point
 * and their pins would stack. Spread only EXACT duplicates around a small ring (~120 m) so each
 * numbered pin is visible. Display-only: distances and ordering come from the server untouched.
 */
export function spreadStackedPins<T extends { lat: number; lng: number }>(
  items: T[],
  radiusDeg = 0.0011,
): (T & { displayLat: number; displayLng: number })[] {
  const groups = new Map<string, number[]>();
  items.forEach((it, i) => {
    const key = `${it.lat.toFixed(6)},${it.lng.toFixed(6)}`;
    groups.set(key, [...(groups.get(key) ?? []), i]);
  });
  const out = items.map((it) => ({
    ...it,
    displayLat: it.lat,
    displayLng: it.lng,
  }));
  for (const idx of groups.values()) {
    if (idx.length < 2) {
      continue;
    }
    idx.forEach((i, k) => {
      const angle = (2 * Math.PI * k) / idx.length;
      out[i].displayLat = items[i].lat + radiusDeg * Math.sin(angle);
      out[i].displayLng =
        items[i].lng +
        (radiusDeg * Math.cos(angle)) /
          Math.cos((items[i].lat * Math.PI) / 180);
    });
  }
  return out;
}

/** The voucher code from the email link: ?code=… (also ?voucher=…). Uppercased, or null. */
export function voucherCodeFromSearch(search: string): string | null {
  const p = new URLSearchParams(search);
  const raw = (p.get("code") ?? p.get("voucher") ?? "").trim().toUpperCase();
  return /^[A-Z0-9-]{4,16}$/.test(raw) ? raw : null;
}

/** A US ZIP (5 digits or ZIP+4) the server will accept. */
export function isValidZip(zip: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(zip.trim());
}

/**
 * What the page does with the ARRIVAL search (opened with ?code=, centered on the voucher holder's ZIP).
 * Apply it when the server centered somewhere (results, or a voucher status like "redeemed" worth
 * showing right away) and prefill that ZIP. Stay quiet — exactly the old behavior — when there was
 * nothing to center on or the search itself failed.
 */
export function autoSearchOutcome(
  resp: LocatorResponse,
): { apply: false } | { apply: true; zip: string | null } {
  if (
    resp.status === "no_holder_zip" ||
    resp.status === "error" ||
    resp.status === "rate_limited" ||
    resp.status === "invalid_request"
  ) {
    return { apply: false };
  }
  const zip =
    typeof resp.auto_zip === "string" && /^\d{5}$/.test(resp.auto_zip)
      ? resp.auto_zip
      : null;
  return { apply: true, zip };
}

/** The line shown above arrival results. */
export function autoSearchHint(zip: string): string {
  return `Locations near ${zip} that accept your voucher.`;
}

// ---- "Use My Location" fills the ZIP field (2026-10-22) -------------------------------------------

/**
 * The ZIP the server derived from the member's coordinates (`near_zip`, nearest ZIP centroid within 30
 * miles — voucher_locator_nearest_zip). Null when absent or malformed: the field then stays empty.
 */
export function nearZipFromResponse(resp: LocatorResponse): string | null {
  return typeof resp.near_zip === "string" && /^\d{5}$/.test(resp.near_zip)
    ? resp.near_zip
    : null;
}

/** The line shown above results found from the member's location. */
export function locationSearchHint(zip: string, voucherMode: boolean): string {
  return voucherMode
    ? `Locations near ${zip} (your current location) that accept your voucher.`
    : `Locations near ${zip} (your current location).`;
}

/**
 * Friendly copy for a failed geolocation request. Codes are GeolocationPositionError's:
 * 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT; null = the browser has no geolocation.
 * Every message ends by pointing back to typing a ZIP, which always keeps working.
 */
export function locationErrorMessage(code: number | null): string {
  switch (code) {
    case null:
      return "Your browser can't share its location. Please type your ZIP code instead.";
    case 1:
      return "Location access is turned off for this site. You can allow it in your browser settings, or just type your ZIP code.";
    case 3:
      return "Finding your location took too long. Please try again, or type your ZIP code.";
    default:
      return "We couldn't determine your location. Please type your ZIP code instead.";
  }
}

// ---- "Redeem in person" notice (2026-10-22) ------------------------------------------------------

export const IN_PERSON_NOTICE = {
  title: "Vouchers must be redeemed in person",
  body: "Bring your voucher (printed or on your phone) to a participating partner location. Vouchers can't be redeemed online or by phone.",
  button: "OK, got it",
  /** The persistent one-liner under the Search button. */
  note: "Vouchers must be redeemed in person at a participating location, not online or by phone.",
} as const;

export const IN_PERSON_NOTICE_KEY = "vl_in_person_notice_dismissed";

type MaybeStorage = Pick<Storage, "getItem" | "setItem"> | null | undefined;

function sessionStore(): MaybeStorage {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null; // the accessor itself throws when site data is blocked
  }
}

/**
 * True when the notice was already dismissed in this browser session. Storage that is missing or
 * throws (private mode, blocked site data) counts as "not seen": the member sees it again, which is
 * harmless; the page never breaks.
 */
export function inPersonNoticeDismissed(
  storage: MaybeStorage = sessionStore(),
): boolean {
  try {
    return storage?.getItem(IN_PERSON_NOTICE_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberInPersonNoticeDismissed(
  storage: MaybeStorage = sessionStore(),
): void {
  try {
    storage?.setItem(IN_PERSON_NOTICE_KEY, "1");
  } catch {
    /* best effort */
  }
}
